import React, { useContext, useEffect, useState } from 'react'
import { DataContext } from '../../context/DataContext'
import { Icon, LoadingDots, SectionTitle } from '@pinflag/pinflag-ui-kit'

import { useAbandonedCart } from '../../hook/useAbandonedCart'
import { MAPBOX_STATIC_MAP_URL } from '../../constants/url'

import AddressUserData from './AddressUserData'
import ManualDirection from './ManualDirection'
import { motion, AnimatePresence } from 'framer-motion'
import { useApiPinflag } from '../../hook/useApiPinflag'

function AddressStep () {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedPointData, setSelectedPointData] = useState(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const {
    steps,
    setAddressInfo,
    setSteps,
    addressInfo,
    setSelectedServiceType,
    setCartData,
    setSelectedShippingMethod,
    userInfo,
    isUserValidated,
    abandonedCartData,
    abandonedCart,
    companyInfo,
    ecommerceInfo,
    pointsData
  } = useContext(DataContext)
  const { primaryColor } = companyInfo
  const { localPoints, pinflagPoints } = pointsData

  const { upsertAbandonedCart } = useAbandonedCart()

  const { getPoints } = useApiPinflag()

  const handleAdvanceStep = () => {
    const updatedSteps = [...steps]
    updatedSteps[0].completed = true
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = true
    setSteps(updatedSteps)
  }

  const mapDesktopUrl = selectedPointData && selectedPointData.center
    ? `${MAPBOX_STATIC_MAP_URL}${selectedPointData.center[0]},${selectedPointData.center[1]},14.1,0,0/440x200?access_token=${companyInfo.mapboxToken}`
    : null

  const setGiftCardProduct = (arrayAddress, aditionalInfo) => {
    updateAddressInfo(arrayAddress, aditionalInfo)
    setFreeServiceType()
    setShippingMethodToUnnecessary()
    markCartAsFreeShipping()
    updateStepsForGiftCard()
  }

  const updateAddressInfo = (arrayAddress, aditionalInfo) => {
    setAddressInfo(prevState => ({
      ...prevState,
      userAddress: arrayAddress[0],
      district: arrayAddress[1],
      region: arrayAddress[2],
      aditionalInfo,
      deliveryType: 'No aplica despacho',
      requireShipping: false,
      price: 0
    }))
  }

  const setFreeServiceType = () => {
    setSelectedServiceType(prevState => ({
      ...prevState,
      price: 0
    }))
  }

  const setShippingMethodToUnnecessary = () => {
    setSelectedShippingMethod('unnecessary')
  }

  const markCartAsFreeShipping = () => {
    setCartData(prevState => ({
      ...prevState,
      isFreeShipping: true
    }))
  }

  const getSectionTitleProps = () => {
    if (isLoading && addressInfo.userAddress) {
      return {
        title: `${addressInfo.userAddress}, ${addressInfo.district}`,
        subtitle: addressInfo.aditionalInfo || '',
        showButton: false,
        buttonLabel: 'Cambiar',
        buttonDisabled: false,
        buttonLoading: false,
        buttonLeftIcon: <Icon name="edit" className="mr-1" width={12} />,
        buttonFontSize: '0.7rem'
      }
    }
    return {
      title: 'Dirección',
      showButton: false
    }
  }

  const updateStepsForGiftCard = () => {
    const updatedSteps = [...steps]
    updatedSteps[0].completed = true
    updatedSteps[1].disabled = false
    updatedSteps[1].subSteps[0].completed = true
    updatedSteps[1].subSteps[1].completed = true
    setSteps(updatedSteps)
  }

  useEffect(() => {
    const updatedAddressInfo = { ...addressInfo, manualDelivery: (userInfo.isRegistered && !isUserValidated) ? false : !selectedPointData }
    setAddressInfo(updatedAddressInfo)
  }, [selectedPointData])

  useEffect(() => {
    if (steps[1].subSteps[1].completed && !abandonedCart) {
      upsertAbandonedCart(abandonedCartData)
    }
  }, [abandonedCartData, steps[1].subSteps[1].completed])

  useEffect(() => {
    if (!localPoints.length && !pinflagPoints.length) {
      getPoints()
    }
  }, [])

  const sectionTitleProps = getSectionTitleProps()

  return (
    <div className='md:h-full bg-slate-100 flex flex-col grow md:w-[517px]'>
      <AnimatePresence>
        {!isLoading && (
          <motion.div
            key="instructionText"
            className="overflow-hidden"
            initial={{ opacity: 1, y: 0, height: 'auto' }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <p className="text-center text-gray-800 text-sm pt-5 font-sm">
              Ingresa una dirección para recibir tu compra
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className={`flex flex-col bg-white rounded-lg mx-[21px] md:px-0 mt-3 ${isLoading ? 'mt-3' : 'pt-1'} ${
          ecommerceInfo.cms === 'woocommerce' && 'md:bg-slate-100'
        }`}
        layout
      >
        <motion.div
          className="mx-[21px] p-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          layout
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={isLoading ? 'loading' : 'default'}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <SectionTitle
                {...sectionTitleProps}
                iconName="home"
                iconCustomStyle={{ fill: 'white', stroke: 'white', strokeWidth: 0.5 }}
                primaryColor={primaryColor}
              />
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <AnimatePresence mode="wait">
          {isLoading
            ? (
              <motion.div
                className='bg-slate-100 pt-2'
                key="loading"
              >
                <LoadingDots label="cargando datos" />
              </motion.div>
              )
            : (
              <motion.div
                key="content"
                className='px-3 md:px-0'
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {!isEditing
                  ? (
                    <AddressUserData
                      setter={setIsEditing}
                      selectedPointData={selectedPointData}
                      mapUrl={mapDesktopUrl}
                      setSelectedPointData={setSelectedPointData}
                      handleAdvanceStep={handleAdvanceStep}
                      setGiftCardProduct={setGiftCardProduct}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                    />
                    )
                  : (
                    <ManualDirection
                      setter={setIsEditing}
                      handleAdvanceStep={handleAdvanceStep}
                      setGiftCardProduct={setGiftCardProduct}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                    />
                    )}
              </motion.div>
              )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

export default AddressStep
